import classnames from "classnames";
import React, { useState } from "react";
import { DashboardChecksTypeEnum } from "@payping/api";
import { Card, Col, Row, Space, Typography, useMessagedAsync } from "@payping/web-components";
import { getUserChecks } from "@payping/redux/actions";
import { Link, LinkProps } from "react-router-dom";
import { webConstants } from "@payping/utils/platformConstants";
import {
  CheckCircleFilled,
  ClockCircleFilled,
  CloseCircleFilled,
  DollarOutlined,
  HomeOutlined,
  ShopOutlined,
  User,
} from "@payping/icons";

import wizardStyle from "@/assets/scss/Wizard.module.scss";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import {
  oldProfileBankId,
  oldProfileBusinessId,
  oldProfileLocalId,
  oldProfilePersonalId,
} from "@/assets/googleTrackIds";
import TaxConsent from "./TaxConsent";

const Profile: React.FC = () => {
  const [visibleConsent, setVisibleConsent] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const checks = useAppSelector((state) => state.user.checks);
  const { t } = useAppTranslation();
  // TODO: this logic should be checked in backend and included inside checks.businessStatus (remove this section when new User service is launched)
  const businessSectionStatus = Math.min(
    checks?.isSiteRequired ? Number(checks.siteStatus) : DashboardChecksTypeEnum.Confirmed,
    Number(checks?.businessStatus),
    Number(checks?.businessRegistrationStatus)
  );
  const personalSectionStatus = Math.min(Number(checks?.personalStatus), Number(checks?.birthCertificateStatus));

  useMessagedAsync(() => dispatch(getUserChecks()), [], { useProgress: true });

  const { DashboardRoutes } = webConstants;
  const CompletedEl = (
    <Space className={"color--success"}>
      <CheckCircleFilled width={"1em"} />
      <Typography.Text variant={"body2"}>{t("common.completed")}</Typography.Text>
    </Space>
  );
  const NotCompletedEl = (
    <Space className={"color--danger"}>
      <CloseCircleFilled width={"1em"} />
      <Typography.Text variant={"body2"}>{t("common.notCompleted")}</Typography.Text>
    </Space>
  );
  const PendingEl = (
    <Space className={"color--warning"}>
      <ClockCircleFilled width={"1em"} />
      <Typography.Text variant={"body2"}>{t("common.awaitingConfirmation")}</Typography.Text>
    </Space>
  );

  const StatusElement = (completed?: DashboardChecksTypeEnum) => {
    switch (completed) {
      case DashboardChecksTypeEnum.NotFilled:
        return NotCompletedEl;
      case DashboardChecksTypeEnum.AdminPending:
        return PendingEl;
      case DashboardChecksTypeEnum.Confirmed:
        return CompletedEl;
      default:
        return null;
    }
  };

  const ConditionalLink = ({ children, to, ...otherProps }: LinkProps) =>
    checks?.agreementAccepted && to ? (
      <Link to={to} {...otherProps}>
        {children}
      </Link>
    ) : (
      <span onClick={() => setVisibleConsent(true)} {...otherProps}>
        {children}
      </span>
    );

  return (
    <Row className={classnames(wizardStyle.profileWrapper)} justify={"center"} style={{ height: "55vh" }}>
      <TaxConsent visible={visibleConsent} setVisibleConsent={setVisibleConsent} />
      <Row gutter={[32, 16]}>
        <Col xs={24} lg={12}>
          <ConditionalLink to={DashboardRoutes.user.profile.personal} data-google-track={oldProfilePersonalId}>
            <Card className={classnames("h-100")}>
              <Card.Content Img={User} direction={"column"} showArrow>
                <Typography.Text color={"primaryColor"} className={"font-rem-11"}>
                  {t("overview.profile.main.personal")}
                </Typography.Text>
                {StatusElement(personalSectionStatus)}
              </Card.Content>
            </Card>
          </ConditionalLink>
        </Col>
        <Col xs={24} lg={12}>
          <ConditionalLink to={DashboardRoutes.user.profile.local} data-google-track={oldProfileLocalId}>
            <Card className="h-100">
              <Card.Content Img={HomeOutlined} direction={"column"} showArrow>
                <Typography.Text color={"primaryColor"} className={"font-rem-11"}>
                  {t("overview.profile.main.local")}
                </Typography.Text>
                {StatusElement(checks?.localStatus)}
              </Card.Content>
            </Card>
          </ConditionalLink>
        </Col>
        <Col xs={24} lg={12}>
          <ConditionalLink to={DashboardRoutes.user.profile.business} data-google-track={oldProfileBusinessId}>
            <Card className="h-100">
              <Card.Content Img={ShopOutlined} direction={"column"} showArrow>
                <Typography.Text color={"primaryColor"} className={"font-rem-11"}>
                  {t("overview.profile.main.business")}
                </Typography.Text>
                {StatusElement(businessSectionStatus)}
              </Card.Content>
            </Card>
          </ConditionalLink>
        </Col>
        <Col xs={24} lg={12}>
          <ConditionalLink to={DashboardRoutes.user.profile.bank} data-google-track={oldProfileBankId}>
            <Card className="h-100">
              <Card.Content Img={DollarOutlined} direction={"column"} showArrow>
                <Typography.Text color={"primaryColor"} className={"font-rem-11"}>
                  {t("overview.profile.main.bank")}
                </Typography.Text>
                {StatusElement(checks?.bankStatus)}
              </Card.Content>
            </Card>
          </ConditionalLink>
        </Col>
      </Row>
    </Row>
  );
};

export default Profile;
